import { Client } from "./websocket.mjs";
import { renderGroup } from "./renderGroup.mjs";
import { renderHome } from "./renderHome.mjs";
import { formatCurrency, getParticipantName } from "./utils.mjs";
import { createTextField } from "./textInput.mjs";
import { createParticipantAvatar } from "./components.mjs";
import router from "./router.mjs";
import { modalRegistry } from "./modalRegistry.mjs";
import { appendModalFooterButton } from "./uiHelpers.mjs";
import { renderLanguagePicker } from "./ui.mjs";
import t, { locale as i18nLocale } from 'virtual:i18n';

export const cache = {
    group: null,
    client: null
}

// Initialize the app when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', async () => {
    document.title = t.appTitle;

    // Check for pending sync full-screen flow first
    if (localStorage.getItem('splitpal_pending_sync') === 'true') {
        const { renderSyncScreen } = await import('./creatorHandoff.mjs');
        renderSyncScreen();
        return; // Halt regular app initialization
    }

    // Track initial page view with possible ref
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref') || 'direct';
    import('./utils.mjs').then(({ trackEvent }) => {
        trackEvent('page_viewed', { ref });
    });

    // Initialize global footer download link
    const downloadAppLink = document.getElementById('download-app-link');
    if (downloadAppLink) {
        // Update text from i18n (the HTML is static, so we override at runtime)
        const [likePart, downloadPart] = t.likeSplitPalDownloadApp.split('?');
        downloadAppLink.querySelector('span').innerHTML = `<span style="color: #888; font-weight: 400;">${likePart}?</span>${downloadPart}`;

        downloadAppLink.addEventListener('mouseenter', () => {
            downloadAppLink.style.opacity = '0.8';
            downloadAppLink.style.transform = 'translateY(-1px)';
        });
        downloadAppLink.addEventListener('mouseleave', () => {
            downloadAppLink.style.opacity = '1';
            downloadAppLink.style.transform = 'translateY(0)';
        });
        downloadAppLink.addEventListener('click', async (e) => {
            e.preventDefault();
            const { trackEvent } = await import('./utils.mjs');
            trackEvent('like_splitpal_download');

            localStorage.setItem('splitpal_app_download_clicked', 'true');

            const { isCreator, startCreatorHandoff } = await import('./creatorHandoff.mjs');
            if (isCreator()) {
                startCreatorHandoff();
            } else {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                const { STORE_LINKS } = await import('./config.mjs');
                const targetUrl = isIOS ? STORE_LINKS.ios : STORE_LINKS.android;
                window.open(targetUrl, '_blank');
            }
        });
    }

    let client = new Client();
    cache.client = client;

    // Setup Back button behavior
    const backBtn = document.getElementById('back-button');
    if (backBtn) {
        backBtn.addEventListener('click', () => {
            const { groupId, expenseId, creditor } = router.getParams();
            if ((expenseId || creditor != null) && groupId) {
                // From expense or payback view → go to group view
                router.navigate(`/home/${groupId}`);
            } else {
                // From group view → go home
                router.navigate('/home');
            }
        });

        // Close the settings sheet when the user presses the OS/browser back button.
        // We push a phantom history entry (same URL) when the sheet opens, so that
        // back-press gives us this popstate event to intercept.
        window.addEventListener('popstate', () => {
            if (modalRegistry.settingsModal) {
                const m = modalRegistry.settingsModal;
                modalRegistry.settingsModal = null;   // clear BEFORE closeModal
                import('./ui.mjs').then(({ closeModal }) => closeModal(m));
                // Note: closeModal (external) does NOT call onClose, so no history.back() loop
            }
        });
    }

    client.onError((err) => {
        console.error('graphQlError', err);
        cache.group = null; // Clear stale data on error
        updateHeader(null); // Reset header
        import('./ui.mjs').then(({ renderError }) => renderError(err));
    });
    client.onSocketError((err) => {
        console.error(err);
        cache.group = null;
        updateHeader(null);
        import('./ui.mjs').then(({ renderError }) => renderError(err.message || 'Connection error'));
    });
    client.onFetchStart(() => {
        import('./utils.mjs').then(({ showSpinner }) => showSpinner());
    });
    client.onFetchEnd(() => {
        import('./utils.mjs').then(({ hideSpinner }) => hideSpinner());
    });
    client.onGroupDataReceived(async (data, isSubscriptionUpdate) => {
        cache.group = data;
        // Keep localStorage cache in sync with live updates
        saveGroupToHomeCache(data);

        const { groupId, expenseId, creditor, debtor } = router.getParams();
        if (!groupId) return;  // On home screen — nothing to update

        updateHeader(cache.group);

        if (creditor != null && debtor != null) {
            // Payback / debt-details view
            const debtorName = getParticipantName(cache.group, debtor);
            const creditorName = getParticipantName(cache.group, creditor);
            updateHeader(cache.group, 'payback', { debtorName, creditorName });
            import('./debtDetails.mjs').then(module => {
                let el = document.getElementById('app-container');
                module.renderDebtDetails(el, cache.group, creditor, debtor);
            });
            return;
        }

        // If a modal registered a live-update handler, let it update itself
        if (isSubscriptionUpdate && modalRegistry.onGroupUpdate) {
            modalRegistry.onGroupUpdate(cache.group);
        }

        // Always refresh the background list
        await renderGroup(cache.group);

        // On first data load (not a live subscription update), open the expense modal
        // if the URL already contains an expenseId (i.e. user landed via a deep link)
        if (!isSubscriptionUpdate && expenseId && !document.getElementById('receipt-view-modal') && !document.getElementById('split-expense-modal')) {
            await openExpenseModal(cache.group, expenseId);
        }
    });

    // Register route change listener with the router
    // This ensures route() is called when router.navigate() is used programmatically
    router.onRouteChange(() => {
        route();
    });
    route();



});


// ─── Route Handlers ────────────────────────────────────────────────────────

function handleClaimRoute(token) {
    updateHeader(null);
    if (cache.client && typeof cache.client.disconnect === 'function') {
        cache.client.disconnect();
    }
    import('./claimPage.mjs').then(module => {
        let el = document.getElementById('app-container');
        module.renderClaimPage(el, token);
    });
}

function handleHomeRoute() {
    updateHeader(null);
    if (cache.client && typeof cache.client.disconnect === 'function') {
        cache.client.disconnect();
    }
    renderHome();
}

async function handleGroupRoute({ groupId, expenseId, creditor, debtor }) {
    // Clear stale content when switching to a different group
    if (cache.group && cache.group.id !== groupId) {
        const appContainer = document.getElementById('app-container');
        if (appContainer) appContainer.innerHTML = '';
        cache.group = null;
    }

    // Connect (idempotent — websocket.mjs guards against duplicate connections)
    cache.client.connect(groupId);

    // If we don't have data yet, stop here; onGroupDataReceived will re-trigger
    if (!cache.group) return;

    if (creditor != null && debtor != null) {
        const debtorName = getParticipantName(cache.group, debtor);
        const creditorName = getParticipantName(cache.group, creditor);
        updateHeader(cache.group, 'payback', { debtorName, creditorName });
        import('./debtDetails.mjs').then(module => {
            let el = document.getElementById('app-container');
            module.renderDebtDetails(el, cache.group, creditor, debtor);
        });
        return;
    }

    updateHeader(cache.group);
    await renderGroup(cache.group);

    // Open expense modal — ONLY here, ONLY on navigation
    if (expenseId) {
        await openExpenseModal(cache.group, expenseId);
    }
}

/**
 * Opens the correct expense modal for a given expenseId.
 * Called only from handleGroupRoute (i.e., only on navigation events).
 */
async function openExpenseModal(group, expenseId) {
    const expense = group.expenses.find(e => e.id === expenseId);
    if (!expense) {
        console.warn(`Expense ${expenseId} not found in group.`);
        return;
    }
    if (expense.expenseType === 'ITEMIZED') {
        const { renderReceiptView } = await import('./receipt.mjs');
        await renderReceiptView(expense, group.participants, true);
    } else {
        const { openSplitExpense } = await import('./splitExpense.mjs');
        openSplitExpense(group, null, expense);
    }
}

function updateBackButton() {
    const backBtn = document.getElementById('back-button');
    if (!backBtn) return;
    const { groupId, expenseId, creditor } = router.getParams();
    // Show back button if we are in group, receipt, or payback views; hide on root home
    backBtn.style.display = (expenseId || groupId || creditor != null) ? 'flex' : 'none';
}

export function updateHeader(group, viewType = 'group', paybackInfo = null) {
    const titleEl = document.getElementById('app-title');
    const assignContainer = document.getElementById('assign-container');

    if (!titleEl) return;

    if (viewType === 'payback' && paybackInfo) {
        titleEl.className = 'header-title-container';
        // In payback/debt details view - show "Debt Details" with debtor → creditor
        titleEl.innerHTML = `
            <div class="payback-header-clickable">
                <div class="payback-header-title">${t.debtDetails}</div>
                <div class="payback-header-subtitle">${paybackInfo.debtorName} &rarr; ${paybackInfo.creditorName}</div>
            </div>
        `;
        // Clear assign container for payback view
        if (assignContainer) {
            assignContainer.innerHTML = '';
        }
    } else if (group) {
        titleEl.className = 'header-title-container';
        // In group view - show group name and total spent
        const groupName = group.name && group.name.trim() ? group.name : 'Unnamed Tab';

        // Use the backend-computed totalSpent (same field the home page uses).
        // Fall back to summing raw expense amounts only if the field is absent.
        const currency = group.currency || 'USD';
        const totalSpent = (group.settlement && typeof group.settlement.totalSpent === 'number')
            ? group.settlement.totalSpent
            : (group.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);

        const formattedTotal = formatCurrency(totalSpent, currency);

        // Update title (centered)
        titleEl.innerHTML = `
            <div class="group-header-clickable" id="group-header-clickable">
                <div class="group-header-title">${groupName}</div>
                <div class="group-header-subtitle">${t.total}: ${formattedTotal}</div>
            </div>
        `;

        // Add menu button to the right side (assign-container)
        if (assignContainer) {
            assignContainer.innerHTML = `
                <div id="group-menu-button" style="width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; border-radius: 50%; transition: background-color 0.2s ease;">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="1.5"></circle>
                        <circle cx="12" cy="5" r="1.5"></circle>
                        <circle cx="12" cy="19" r="1.5"></circle>
                    </svg>
                </div>
            `;

            // Add click handler for menu button
            const menuButton = document.getElementById('group-menu-button');
            if (menuButton) {
                menuButton.addEventListener('mouseenter', () => {
                    menuButton.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                });
                menuButton.addEventListener('mouseleave', () => {
                    menuButton.style.backgroundColor = 'transparent';
                });
                menuButton.addEventListener('click', (e) => {
                    e.stopPropagation();
                    openGroupMenu(group);
                });
            }
        }

        // Add click handler to open edit modal
        const clickableHeader = document.getElementById('group-header-clickable');
        if (clickableHeader) {
            clickableHeader.addEventListener('click', () => {
                openEditGroupNameModal(group);
            });
        }
    } else {
        titleEl.className = 'logo';
        // In home view - show default title with logo
        titleEl.innerHTML = `
            <img src="/logo.svg" alt="SplitPal Logo" class="logo-icon">
            <div style="font-size: 24px; font-weight: 700; color: #444;">${t.appTitle}</div>
        `;
        
        // Add functional Language Picker after the title
        renderLanguagePicker(titleEl, 'header');

        // Add "New Tab" button to assign container for home view
        if (assignContainer) {
            assignContainer.innerHTML = '';

            // New Tab Button
            const newTabBtn = document.createElement('button');
            newTabBtn.className = 'header-action-text-btn';
            newTabBtn.style.color = 'var(--color-primary)';
            newTabBtn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right:2px"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg><span>${t.newTab}</span>`;
            newTabBtn.onclick = async () => {
                const { showCreateTabModal } = await import('./createTab.mjs');
                showCreateTabModal();
            };
            assignContainer.appendChild(newTabBtn);
        }
    }
}

async function openEditGroupNameModal(group) {
    const { renderModal } = await import('./ui.mjs');
    const { renameGroup } = await import('./api.mjs');
    const { createPrimaryButton } = await import('./components.mjs');

    const originalName = group.name || '';

    // Create input field
    const inputContainer = document.createElement('div');
    inputContainer.style.padding = '10px 0';

    const label = document.createElement('label');
    label.textContent = t.groupName;
    label.style.display = 'block';
    label.style.marginBottom = '8px';
    label.style.fontWeight = '600';
    label.style.fontSize = '14px';

    const input = createTextField({
        value: originalName,
        placeholder: t.enterGroupName,
        onSubmit: () => updateButton.click()
    });
    input.maxLength = 25;
    input.style.width = '100%';
    input.style.padding = '12px';
    input.style.fontSize = '16px';
    input.style.border = '1px solid #ddd';
    input.style.borderRadius = '8px';
    input.style.boxSizing = 'border-box';

    const charCount = document.createElement('div');
    charCount.style.fontSize = '12px';
    charCount.style.color = '#888';
    charCount.style.marginTop = '4px';
    charCount.style.textAlign = 'right';
    charCount.textContent = `${input.value.length}/25`;

    inputContainer.appendChild(label);
    inputContainer.appendChild(input);
    inputContainer.appendChild(charCount);

    const modal = renderModal({
        title: t.editGroupName,
        body: inputContainer,
        buttons: []
    });

    const updateButton = createPrimaryButton({
        text: t.update,
        disabled: true,
        onClick: async () => {
            const newName = input.value.trim();
            if (newName && newName !== originalName) {
                try {
                    const updatedGroup = await renameGroup(group.id, newName);
                    cache.group = updatedGroup;
                    updateHeader(updatedGroup);
                    const { closeModal } = await import('./ui.mjs');
                    closeModal(modal);
                } catch (error) {
                    console.error('Failed to rename group:', error);
                    alert(t.unknownErrorHasOccured);
                }
            } else if (!newName) {
                alert(t.groupNameCannotBeEmpty);
            }
        }
    });

    // Function to check if name has changed and update button state
    function updateButtonState() {
        const currentName = input.value.trim();
        const hasChanged = currentName !== originalName && currentName.length > 0;
        updateButton.disabled = !hasChanged;
        updateButton.style.opacity = hasChanged ? '1' : '0.5';
        updateButton.style.cursor = hasChanged ? 'pointer' : 'not-allowed';
    }

    input.addEventListener('input', () => {
        charCount.textContent = `${input.value.length}/25`;
        updateButtonState();
    });

    appendModalFooterButton(modal, updateButton);

    setTimeout(() => input.focus(), 100);
}

async function openGroupMenu(group) {
    const { renderModal, closeModal } = await import('./ui.mjs');
    const { createMenuItem, Icons } = await import('./components.mjs');

    const menuContainer = document.createElement('div');
    menuContainer.style.display = 'flex';
    menuContainer.style.flexDirection = 'column';
    menuContainer.style.gap = '8px';
    menuContainer.style.padding = '8px 0';

    const menuItems = [
        {
            icon: Icons.users,
            label: t.participants,
            onClick: () => { closeModal(modal); openParticipantsModal(group); }
        },
        {
            icon: Icons.currency,
            label: `${t.baseCurrency} (${group.currency || 'USD'})`,
            onClick: () => { closeModal(modal); openBaseCurrencyModal(group); }
        }
    ];

    menuItems.forEach(item => menuContainer.appendChild(createMenuItem(item)));

    // ── "Minimize transactions" pill toggle ──────────────────────────────────
    if (group.expenses && group.expenses.length > 0) {
        const TEAL = '#00897B';
        let isSimplified = group.groupSettings?.simplify === true;

        const toggleRow = document.createElement('div');
        Object.assign(toggleRow.style, {
            display: 'flex',
            alignItems: 'center',
            padding: '14px 16px',
            gap: '16px',
            cursor: 'pointer',
            userSelect: 'none',
            borderRadius: '12px',
            transition: 'background-color 0.15s',
        });
        toggleRow.addEventListener('mouseenter', () => { toggleRow.style.backgroundColor = 'rgba(0,0,0,0.04)'; });
        toggleRow.addEventListener('mouseleave', () => { toggleRow.style.backgroundColor = 'transparent'; });

        // Icon — use a compress/minimize SVG
        const iconWrap = document.createElement('div');
        Object.assign(iconWrap.style, { width: '24px', height: '24px', flexShrink: '0', color: '#49454F' });
        iconWrap.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="4 14 10 14 10 20"></polyline>
            <polyline points="20 10 14 10 14 4"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
            <line x1="3" y1="21" x2="14" y2="10"></line>
        </svg>`;

        // Label
        const labelEl = document.createElement('div');
        labelEl.textContent = t.minimizeTransactions;
        Object.assign(labelEl.style, {
            flex: '1',
            fontSize: '16px',
            color: '#49454F',
        });

        // Pill
        const pillOuter = document.createElement('div');
        Object.assign(pillOuter.style, {
            width: '44px',
            height: '24px',
            borderRadius: '12px',
            backgroundColor: isSimplified ? TEAL : '#ccc',
            position: 'relative',
            transition: 'background-color 0.2s',
            flexShrink: '0',
        });
        const pillThumb = document.createElement('div');
        Object.assign(pillThumb.style, {
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            position: 'absolute',
            top: '2px',
            left: isSimplified ? '22px' : '2px',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        });
        pillOuter.appendChild(pillThumb);

        toggleRow.appendChild(iconWrap);
        toggleRow.appendChild(labelEl);
        toggleRow.appendChild(pillOuter);

        let pending = false;
        toggleRow.addEventListener('click', async () => {
            if (pending) return;
            pending = true;

            const newValue = !isSimplified;
            // Optimistic flip
            pillThumb.style.left = newValue ? '22px' : '2px';
            pillOuter.style.backgroundColor = newValue ? TEAL : '#ccc';

            try {
                const { setGroupSettings } = await import('./api.mjs');
                const updatedGroup = await setGroupSettings(group.id, { simplify: newValue });
                cache.group = updatedGroup;
                isSimplified = newValue;
                router.navigate(window.location.pathname);
            } catch (err) {
                console.error('Failed to update group settings:', err);
                // Revert
                pillThumb.style.left = isSimplified ? '22px' : '2px';
                pillOuter.style.backgroundColor = isSimplified ? TEAL : '#ccc';
                alert('Failed to update setting. Please try again.');
            } finally {
                pending = false;
            }
        });

        menuContainer.appendChild(toggleRow);
    }

    const coffeeIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
        <line x1="6" y1="2" x2="6" y2="4"></line>
        <line x1="10" y1="2" x2="10" y2="4"></line>
        <line x1="14" y1="2" x2="14" y2="4"></line>
    </svg>`;

    const coffeeItem = createMenuItem({
        icon: coffeeIcon,
        label: 'Buy us a coffee',
        onClick: () => {
            closeModal(modal);
            window.open('https://buymeacoffee.com/splitpal', '_blank');
        }
    });
    menuContainer.appendChild(coffeeItem);

    const leaveGroupItem = createMenuItem({
        icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
        </svg>`,
        label: t.leaveGroup,
        onClick: () => {
            closeModal(modal);
            if (confirm(t.areYouSure)) {
                try {
                    const LS_GROUPS_KEY = 'splitpal:groups';
                    const raw = localStorage.getItem(LS_GROUPS_KEY);
                    if (raw) {
                        let groups = JSON.parse(raw);
                        groups = groups.filter(g => g.id !== group.id);
                        localStorage.setItem(LS_GROUPS_KEY, JSON.stringify(groups));
                    }
                } catch (e) {
                    console.error('Failed to update localStorage', e);
                }
                router.navigate('/');
            }
        }
    });
    // Tint both icon and label text for non-default colors
    leaveGroupItem.querySelectorAll('div').forEach(d => { d.style.color = '#B3261E'; });
    menuContainer.appendChild(leaveGroupItem);

    // Download App Section
    const downloadSection = document.createElement('div');
    downloadSection.className = 'app-download-footer-section';
    downloadSection.innerHTML = `
        <div class="app-download-title">${t.likeSplitPalDownloadApp}</div>
        <div class="app-badges">
            <a href="https://apps.apple.com/us/app/splitpal-tabs/id6748889469" class="app-badge" target="_blank">
                <div class="app-badge-icon">
                    <svg viewBox="0 0 384 512" width="20" height="20" fill="currentColor">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-48.7-22.7-77.9-22-34.8.5-73.3 22-89.8 54.3-34.6 62.7-8.9 157.9 24.3 207 16.2 24.3 35.6 51.6 62.1 50.6 25.6-1 35.3-16.5 66.2-16.5 31.1 0 40.1 16.5 66.8 15.9 27.1-.5 44-24.6 60.1-48.8 18.8-26.9 26.3-52.9 26.6-54.2-.6-.3-51.6-20.2-51.8-77.6zm-59.2-198.8c16.2-19.5 27.9-46.7 24.9-74-23.7 1-52.7 15.8-69.7 35.8-15 17.5-28.1 44.9-24.8 72 26.4 2 53.4-14.3 69.6-33.8z"/>
                    </svg>
                </div>
                <div class="app-badge-text">
                    <span>Download on the</span>
                    <strong>App Store</strong>
                </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.legendarybytes.splitpal.tabs" class="app-badge" target="_blank">
                <div class="app-badge-icon">
                    <svg viewBox="0 0 512 512" width="20" height="20" fill="currentColor">
                        <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58-33.3-60.7 60.7 60.7 60.7 58-33.3c21.5-12.3 21.5-32.5 0-44.8zm-86.4 65.2L104.6 499l220.7-126.7 60.7-60.7z"/>
                    </svg>
                </div>
                <div class="app-badge-text">
                    <span>Get it on</span>
                    <strong>Google Play</strong>
                </div>
            </a>
        </div>
    `;
    menuContainer.appendChild(downloadSection);

    // Push a phantom history entry (same URL) so the OS back button has
    // something to pop. The popstate listener (set up in DOMContentLoaded)
    // will catch that pop and close the sheet.
    history.pushState({ modal: 'settings' }, '');

    const modal = renderModal({
        title: t.groupSettings,
        body: menuContainer,
        buttons: [],
        onClose: () => {
            // Only pop the phantom entry if the sheet is still registered
            // (i.e. it was dismissed by swipe/tap, not by the back button).
            // If back-press closed it, the popstate already consumed the entry
            // AND cleared modalRegistry.settingsModal before calling closeModal.
            if (modalRegistry.settingsModal) {
                modalRegistry.settingsModal = null;
                history.back(); // consume the phantom entry
            }
        }
    });

    // Track so the popstate handler can find and close it
    modalRegistry.settingsModal = modal;
}

/**
 * Builds the linked participant map: participantIndex => linkedParticipantGroupIndex
 */
function buildLinkedParticipantMap(group) {
    const map = {};
    const lpGroups = group.settlement && group.settlement.linkedParticipantGroups || [];
    lpGroups.forEach((lpg, groupIdx) => {
        (lpg.participants || []).forEach(pIdx => {
            map[pIdx] = groupIdx;
        });
    });
    return map;
}

export async function openParticipantsModal(group) {
    const { renderModal, closeModal } = await import('./ui.mjs');

    // Build the linked participant map once
    const lpGroups = group.settlement && group.settlement.linkedParticipantGroups || [];
    const linkedParticipantMap = buildLinkedParticipantMap(group);

    // Create participants list
    const participantsContainer = document.createElement('div');
    participantsContainer.style.display = 'flex';
    participantsContainer.style.flexDirection = 'column';
    participantsContainer.style.gap = '12px';
    participantsContainer.style.padding = '16px 0';

    if (group.participants && group.participants.length > 0) {
        group.participants.forEach((participant, index) => {
            // Determine link status for this participant
            const lpGroupIdx = linkedParticipantMap[index];
            const lpGroup = lpGroups[lpGroupIdx];
            const linkedMembers = lpGroup ? (lpGroup.participants || []) : [];
            const isLinked = linkedMembers.length > 1;

            // Build "linked with" label
            let linkedWithLabel = '';
            if (isLinked) {
                const otherMembers = linkedMembers.filter(p => p !== index);
                if (otherMembers.length === 1) {
                    linkedWithLabel = group.participants[otherMembers[0]] || '';
                } else {
                    linkedWithLabel = t.others.replace('{value}', otherMembers.length);
                }
            }

            const participantItem = document.createElement('div');
            participantItem.style.cssText = `
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 12px;
                background-color: #f5f5f5;
                border-radius: 8px;
                position: relative;
            `;

            // Avatar
            const avatar = createParticipantAvatar(participant);
            avatar.style.flexShrink = '0';
            participantItem.appendChild(avatar);

            // Name + optional linked badge
            const nameCol = document.createElement('div');
            nameCol.style.cssText = 'flex: 1; min-width: 0;';

            const nameEl = document.createElement('div');
            nameEl.style.cssText = 'font-size: 16px; font-weight: 500; color: #1D1B20; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
            nameEl.textContent = participant;
            nameCol.appendChild(nameEl);

            if (isLinked) {
                const badge = document.createElement('div');
                badge.style.cssText = `
                    display: flex; align-items: center; gap: 4px;
                    margin-top: 3px;
                    font-size: 12px; color: var(--color-primary); font-weight: 500;
                `;
                badge.innerHTML = `
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                    <span>${t.linkedWith.replace('{value}', linkedWithLabel)}</span>
                `;
                nameCol.appendChild(badge);
            }
            participantItem.appendChild(nameCol);

            // Kebab menu button
            const menuBtn = document.createElement('div');
            menuBtn.className = 'participant-menu-button';
            menuBtn.dataset.index = index;
            menuBtn.style.cssText = `
                width: 32px; height: 32px; display: flex; align-items: center;
                justify-content: center; cursor: pointer; border-radius: 50%;
                transition: background-color 0.2s ease; flex-shrink: 0;
            `;
            menuBtn.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="1.5"></circle>
                    <circle cx="12" cy="5" r="1.5"></circle>
                    <circle cx="12" cy="19" r="1.5"></circle>
                </svg>
            `;
            menuBtn.addEventListener('mouseenter', () => { menuBtn.style.backgroundColor = 'rgba(0,0,0,0.05)'; });
            menuBtn.addEventListener('mouseleave', () => { menuBtn.style.backgroundColor = 'transparent'; });
            menuBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                openParticipantMenu(group, participant, index, modal, isLinked);
            });
            participantItem.appendChild(menuBtn);

            participantsContainer.appendChild(participantItem);
        });
    } else {
        participantsContainer.innerHTML = `<div style="text-align: center; color: #888; padding: 20px;">${t.noParticipantsYet}</div>`;
    }

    // Create custom title with action button
    const titleContainer = document.createElement('div');
    titleContainer.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding-right: 8px;
    `;

    const titleText = document.createElement('span');
    titleText.textContent = t.participants;
    titleContainer.appendChild(titleText);

    // Add participant button
    const addButton = document.createElement('div');
    addButton.style.cssText = `
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: 50%;
        transition: background-color 0.2s ease;
    `;
    addButton.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="8.5" cy="7" r="4"></circle>
            <line x1="20" y1="8" x2="20" y2="14"></line>
            <line x1="23" y1="11" x2="17" y2="11"></line>
        </svg>
    `;
    addButton.addEventListener('mouseenter', () => { addButton.style.backgroundColor = 'rgba(103, 80, 164, 0.08)'; });
    addButton.addEventListener('mouseleave', () => { addButton.style.backgroundColor = 'transparent'; });
    addButton.addEventListener('click', () => { openAddParticipantModal(group, modal); });
    titleContainer.appendChild(addButton);

    const modal = renderModal({
        title: titleContainer,
        body: participantsContainer,
        buttons: []
    });

    return modal;
}

async function openAddParticipantModal(group, participantsModal) {
    const { renderModal, closeModal } = await import('./ui.mjs');
    const { addParticipant } = await import('./api.mjs');
    const { createTextInput, createPrimaryButton } = await import('./components.mjs');

    const { container: inputContainer, input } = createTextInput({
        label: t.participantName,
        value: '',
        placeholder: t.enterParticipantName,
        maxLength: 50
    });

    const modal = renderModal({
        title: t.addParticipant,
        body: inputContainer,
        buttons: []
    });

    const addButton = createPrimaryButton({
        text: t.add,
        disabled: true,
        onClick: async () => {
            const name = input.value.trim();
            if (name) {
                try {
                    const updatedGroup = await addParticipant(group.id, name);
                    cache.group = updatedGroup;
                    updateHeader(updatedGroup);
                    closeModal(modal);
                    if (participantsModal) closeModal(participantsModal);
                    openParticipantsModal(updatedGroup);
                } catch (error) {
                    console.error('Failed to add participant:', error);
                    alert(t.failedToAddParticipant);
                }
            } else {
                alert(t.participantNameCannotBeEmpty);
            }
        }
    });

    function updateButtonState() {
        const isValid = input.value.trim().length > 0;
        addButton.disabled = !isValid;
        addButton.style.opacity = isValid ? '1' : '0.5';
        addButton.style.cursor = isValid ? 'pointer' : 'not-allowed';
    }

    input.addEventListener('input', updateButtonState);
    appendModalFooterButton(modal, addButton);
    setTimeout(() => input.focus(), 100);
    updateButtonState();
}


async function openParticipantMenu(group, participantName, participantIndex, participantsModal, isLinked) {
    const { renderModal, closeModal } = await import('./ui.mjs');

    // Create menu container
    const menuContainer = document.createElement('div');
    menuContainer.style.display = 'flex';
    menuContainer.style.flexDirection = 'column';
    menuContainer.style.gap = '8px';
    menuContainer.style.padding = '8px 0';

    // Menu items – Link vs Unlink changes based on isLinked state
    const linkItem = isLinked
        ? {
            icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18.84 12.25l1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71"></path>
                <path d="M5.17 11.75l-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71"></path>
                <line x1="8" y1="2" x2="8" y2="5"></line>
                <line x1="2" y1="8" x2="5" y2="8"></line>
                <line x1="16" y1="19" x2="16" y2="22"></line>
                <line x1="19" y1="16" x2="22" y2="16"></line>
            </svg>`,
            label: t.unlinkParticipants,
            color: '#B3261E',
            onClick: async () => {
                closeModal(modal);
                const confirmed = confirm(t.unlinkParticipantConfirm.replace('{name}', participantName));
                if (confirmed) {
                    try {
                        const { unlinkSingleParticipant } = await import('./api.mjs');
                        const updatedGroup = await unlinkSingleParticipant(group.id, participantIndex);
                        cache.group = updatedGroup;
                        updateHeader(updatedGroup);
                        if (participantsModal) closeModal(participantsModal);
                        openParticipantsModal(updatedGroup);
                    } catch (error) {
                        console.error('Failed to unlink participant:', error);
                        alert(t.failedToUnlinkParticipant.replace('{error}', error.message));
                    }
                }
            }
        }
        : {
            icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>`,
            label: t.linkParticipants,
            color: '#49454F',
            onClick: () => {
                closeModal(modal);
                openLinkParticipantModal(group, participantName, participantIndex, participantsModal);
            }
        };

    const menuItems = [
        {
            icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>`,
            label: t.rename,
            color: '#49454F',
            onClick: () => {
                closeModal(modal);
                openRenameParticipantModal(group, participantName, participantIndex, participantsModal);
            }
        },
        linkItem,
        {
            icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>`,
            label: t.remove,
            color: '#B3261E',
            onClick: async () => {
                closeModal(modal);
                const confirmed = confirm(t.removeParticipantConfirm.replace('{name}', participantName));
                if (confirmed) {
                    try {
                        const { removeParticipant } = await import('./api.mjs');
                        const updatedGroup = await removeParticipant(group.id, participantIndex);
                        cache.group = updatedGroup;
                        updateHeader(updatedGroup);
                        if (participantsModal) closeModal(participantsModal);
                        openParticipantsModal(updatedGroup);
                    } catch (error) {
                        console.error('Failed to remove participant:', error);
                        alert(t.failedToRemoveParticipant.replace('{error}', error.message));
                    }
                }
            }
        }
    ];

    // Create menu items (color-aware: wrap in a div to tint both icon and label)
    const { createMenuItem } = await import('./components.mjs');
    menuItems.forEach(item => {
        const el = createMenuItem(item);
        // Tint both icon and label text for non-default colors
        if (item.color && item.color !== '#49454F') {
            el.querySelectorAll('div').forEach(d => { d.style.color = item.color; });
        }
        menuContainer.appendChild(el);
    });

    const modal = renderModal({
        title: participantName,
        body: menuContainer,
        buttons: []
    });
}

async function openRenameParticipantModal(group, participantName, participantIndex, participantsModal) {
    const { renderModal, closeModal } = await import('./ui.mjs');
    const { renameParticipant } = await import('./api.mjs');
    const { createTextInput, createPrimaryButton } = await import('./components.mjs');

    const originalName = participantName;

    const { container: inputContainer, input } = createTextInput({
        label: t.participantName,
        value: participantName,
        placeholder: t.enterParticipantName,
        maxLength: 50
    });

    const modal = renderModal({
        title: t.renameParticipant,
        body: inputContainer,
        buttons: []
    });

    const updateButton = createPrimaryButton({
        text: t.update,
        disabled: true,
        onClick: async () => {
            const newName = input.value.trim();
            if (newName && newName !== originalName) {
                try {
                    const updatedGroup = await renameParticipant(group.id, participantIndex, newName);
                    cache.group = updatedGroup;
                    updateHeader(updatedGroup);
                    closeModal(modal);
                    if (participantsModal) closeModal(participantsModal);
                    openParticipantsModal(updatedGroup);
                } catch (error) {
                    console.error('Failed to rename participant:', error);
                    alert(t.failedToRenameParticipant);
                }
            } else if (!newName) {
                alert(t.participantNameCannotBeEmpty);
            }
        }
    });

    function updateButtonState() {
        const currentName = input.value.trim();
        const hasChanged = currentName !== originalName && currentName.length > 0;
        updateButton.disabled = !hasChanged;
        updateButton.style.opacity = hasChanged ? '1' : '0.5';
        updateButton.style.cursor = hasChanged ? 'pointer' : 'not-allowed';
    }

    input.addEventListener('input', updateButtonState);
    appendModalFooterButton(modal, updateButton);
    setTimeout(() => input.focus(), 100);
    updateButtonState();
}

/**
 * Opens a sheet listing all other linked participant groups to link this participant into.
 * Mirrors Flutter's LinkParticipantSheet behaviour.
 */
async function openLinkParticipantModal(group, participantName, participantIndex, participantsModal) {
    const { renderModal, closeModal } = await import('./ui.mjs');

    const lpGroups = group.settlement && group.settlement.linkedParticipantGroups || [];

    // Build the list of linkable targets (groups with at least one other participant)
    // Exclude the group that only contains this participant (that's their own solo group)
    const targets = lpGroups
        .map((lpg, idx) => ({ lpg, idx }))
        .filter(({ lpg }) => {
            const members = lpg.participants || [];
            // Exclude their own solo group and groups that already include them
            if (members.length === 1 && members[0] === participantIndex) return false;
            if (members.includes(participantIndex)) return false;
            return true;
        });

    const bodyContainer = document.createElement('div');
    bodyContainer.style.cssText = 'display: flex; flex-direction: column;';

    // Intro text
    const intro = document.createElement('div');
    intro.style.cssText = 'padding: 16px 16px 8px; font-size: 14px; color: #666; line-height: 1.5;';
    intro.textContent = t.linkParticipantDescription;
    bodyContainer.appendChild(intro);

    if (targets.length === 0) {
        const empty = document.createElement('div');
        empty.style.cssText = 'padding: 32px 16px; text-align: center; color: #888; font-size: 15px;';
        empty.textContent = t.noOtherParticipantsToLink;
        bodyContainer.appendChild(empty);
    } else {
        targets.forEach(({ lpg, idx: targetIdx }) => {
            const members = lpg.participants || [];
            // Build a display name for this group/participant
            const memberNames = members.map(p => group.participants[p]).join(' & ');

            const row = document.createElement('div');
            row.style.cssText = `
                display: flex; align-items: center; gap: 14px;
                padding: 14px 16px; cursor: pointer;
                border-bottom: 1px solid #F3F0F4;
                transition: background-color 0.15s ease;
            `;

            // Avatar cluster
            const avatarWrapper = document.createElement('div');
            avatarWrapper.style.cssText = 'position: relative; width: 44px; height: 44px; flex-shrink: 0;';

            if (members.length === 1) {
                const av = document.createElement('div');
                av.style.cssText = `
                    width: 44px; height: 44px; border-radius: 50%;
                    background-color: var(--color-primary); color: white;
                    display: flex; align-items: center; justify-content: center;
                    font-weight: 600; font-size: 18px;
                `;
                av.textContent = (group.participants[members[0]] || '?').charAt(0).toUpperCase();
                avatarWrapper.appendChild(av);
            } else {
                // Stacked mini-avatars for a group
                const colors = ['#1E66C2', '#7B6FAE', '#9C8CC4'];
                members.slice(0, 2).forEach((p, i) => {
                    const av = document.createElement('div');
                    const size = 32;
                    av.style.cssText = `
                        position: absolute;
                        width: ${size}px; height: ${size}px; border-radius: 50%;
                        background-color: ${colors[i % colors.length]}; color: white;
                        display: flex; align-items: center; justify-content: center;
                        font-weight: 600; font-size: 13px;
                        border: 2px solid white;
                        top: ${i * 12}px; left: ${i * 12}px;
                    `;
                    av.textContent = (group.participants[p] || '?').charAt(0).toUpperCase();
                    avatarWrapper.appendChild(av);
                });
            }
            row.appendChild(avatarWrapper);

            // Name
            const nameEl = document.createElement('div');
            nameEl.style.cssText = 'flex: 1; font-size: 16px; font-weight: 500; color: #1D1B20;';
            nameEl.textContent = memberNames;
            row.appendChild(nameEl);

            // Chevron
            row.insertAdjacentHTML('beforeend', `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C4C4C4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            `);

            row.addEventListener('mouseenter', () => { row.style.backgroundColor = 'rgba(var(--color-primary-rgb), 0.06)'; });
            row.addEventListener('mouseleave', () => { row.style.backgroundColor = 'transparent'; });

            row.addEventListener('click', async () => {
                closeModal(linkModal);
                try {
                    const { linkSingleParticipant } = await import('./api.mjs');
                    const updatedGroup = await linkSingleParticipant(group.id, participantIndex, targetIdx);
                    cache.group = updatedGroup;
                    updateHeader(updatedGroup);
                    if (participantsModal) closeModal(participantsModal);
                    openParticipantsModal(updatedGroup);
                } catch (error) {
                    console.error('Failed to link participant:', error);
                    alert(`Failed to link participant: ${error.message}`);
                }
            });

            bodyContainer.appendChild(row);
        });
    }

    const linkModal = renderModal({
        title: t.linkName.replace('{name}', participantName),
        body: bodyContainer,
        buttons: []
    });
}

async function openBaseCurrencyModal(group) {
    const { renderModal, closeModal } = await import('./ui.mjs');
    const { changeBaseCurrency } = await import('./api.mjs');
    const { openCurrencyPicker } = await import('./currencyPicker.mjs');
    const { createPrimaryButton } = await import('./components.mjs');

    // Get currency from group
    const currency = group.currency || 'USD';

    // Get currency symbol using formatCurrency
    const currencySymbol = formatCurrency(0, currency).replace(/[\d.,\s]/g, '');

    // Get currency name
    // Get currency name using the current locale
    const currencyNames = new Intl.DisplayNames([i18nLocale], { type: 'currency' });
    const currencyName = currencyNames.of(currency);

    // Create currency display
    const currencyContainer = document.createElement('div');
    currencyContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 32px 16px;
    `;

    currencyContainer.innerHTML = `
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" style="display: flex; align-items: center; justify-content: center; border-radius: 50%; background-color: #E8DEF8; padding: 20px; box-sizing: border-box;">
            <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="20" font-weight="700" fill="var(--color-primary)">${currencySymbol}</text>
        </svg>
        <div style="font-size: 32px; font-weight: 700; color: #1D1B20;">${currency}</div>
        <div style="font-size: 16px; font-weight: 500; color: #49454F;">${currencyName}</div>
        <div style="font-size: 14px; color: #666; text-align: center;">${t.thisIsTheBaseCurrency}</div>
    `;

    const changeButton = createPrimaryButton({
        text: t.change,
        onClick: () => {
            openCurrencyPicker(currency, async (newCurrency) => {
                if (newCurrency && newCurrency !== currency) {
                    try {
                        const updatedGroup = await changeBaseCurrency(group.id, newCurrency);
                        cache.group = updatedGroup;
                        updateHeader(updatedGroup);
                        closeModal(modal);
                        // Re-open with new currency to show updated state
                        openBaseCurrencyModal(updatedGroup);
                    } catch (error) {
                        console.error('Failed to change currency:', error);
                        alert('Failed to change currency. Please try again.');
                    }
                }
            });
        }
    });

    const modal = renderModal({
        title: t.baseCurrency,
        body: currencyContainer,
        buttons: []
    });

    appendModalFooterButton(modal, changeButton);
}


const LS_GROUPS_KEY = 'splitpal:groups';
function saveGroupToHomeCache(group) {
    try {
        if (!group || !group.id) return;
        const raw = localStorage.getItem(LS_GROUPS_KEY);
        const list = raw ? JSON.parse(raw) : [];
        const arr = Array.isArray(list) ? list : [];
        const idx = arr.findIndex(g => g && g.id === group.id);

        // Only update if incoming group is newer (or doesn't exist yet)
        if (idx >= 0) {
            const existingGroup = arr[idx];
            // Compare mv (model version) - only update if incoming is newer
            if (group.mv !== undefined && existingGroup.mv !== undefined) {
                if (group.mv <= existingGroup.mv) {
                    console.log('Skipping localStorage update - existing group is newer or same', {
                        incoming: group.mv,
                        existing: existingGroup.mv
                    });
                    return;
                }
            }
            arr[idx] = group;
        } else {
            arr.push(group);
        }
        localStorage.setItem(LS_GROUPS_KEY, JSON.stringify(arr));
    } catch (e) {
        console.warn('Failed to update home cache', e);
    }
}




function route() {
    const { token, groupId, expenseId, creditor, debtor } = router.getParams();

    updateBackButton();

    // Always restore footer visibility on route change
    const globalFooter = document.getElementById('global-footer');
    if (globalFooter) {
        globalFooter.style.display = 'flex';
    }

    // If we're navigating away from an expense view (no expenseId), close any open expense modals
    if (!expenseId) {
        const receiptModal = document.getElementById('receipt-view-modal');
        const splitModal = document.getElementById('split-expense-modal');
        if (receiptModal || splitModal) {
            import('./ui.mjs').then(({ closeModal }) => {
                if (receiptModal) closeModal(receiptModal, /* skipCallback */ true);
                if (splitModal) closeModal(splitModal, /* skipCallback */ true);
            });
            // Also clean up live-update registry
            modalRegistry.onGroupUpdate = null;
        }
    }

    if (token) {
        handleClaimRoute(token);
        return;
    }

    if (!groupId) {
        handleHomeRoute();
        return;
    }

    handleGroupRoute({ groupId, expenseId, creditor, debtor });
}